'use client';

import type { Table } from '@tanstack/react-table';
import { ChevronsUpDownIcon as CaretSortIcon, CheckIcon, SlidersHorizontalIcon as MixerHorizontalIcon } from 'lucide-react';
import * as React from 'react';

import { cn, toSentenceCase } from '../../utils';
import { Button } from '../button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../command';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  className?: string | undefined;
}

export function DataTableViewOptions<TData>({ table, className }: DataTableViewOptionsProps<TData>) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          aria-label='Toggle columns'
          variant='outline'
          role='combobox'
          className={cn('ml-auto hidden gap-2 focus:outline-none focus:ring-1 focus:ring-ring focus-visible:ring-0 lg:flex', className)}
        >
          <MixerHorizontalIcon className='size-4' />
          View
          <CaretSortIcon className='ml-auto size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        className='w-44 p-0'
        onCloseAutoFocus={() => {
          triggerRef.current?.focus();
        }}
      >
        <Command>
          <CommandInput placeholder='Search columns...' />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            <CommandGroup>
              {table
                .getAllColumns()
                .filter(column => typeof column.accessorFn !== 'undefined' && column.getCanHide())
                .map(column => (
                  <CommandItem
                    key={column.id}
                    onSelect={() => {
                      column.toggleVisibility(!column.getIsVisible());
                    }}
                  >
                    <span className='truncate'>{toSentenceCase(column.id)}</span>
                    <CheckIcon className={cn('ml-auto size-4 shrink-0', column.getIsVisible() ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
